import React from "react";
import { Box, Typography, Card, CardContent, Avatar } from "@mui/material";
import moment from "moment";
import { ReactComponent as SmallDhiramIcon } from "../../../../assets/svg/SmallDhiramIcon.svg";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import { useNavigate, useParams } from "react-router-dom";

const statusCategories = [
  { label: "Not started", key: "pending", color: "#9CA3AF" },
  { label: "Ongoing", key: "ongoing", color: "#3B82F6" },
  { label: "Delayed", key: "delayed", color: "#EF4444" },
  { label: "Complete", key: "complete", color: "#22C55E" },
  { label: "In Review", key: "in_review", color: "#EAB308" },
  { label: "Request for Change", key: "request_for_change", color: "#F97316" },
  { label: "Delivered", key: "delivered", color: "#A855F7" },
];

const ActivitiesKanbanView = ({ data, details }) => {
  const flattenActivities = (data || []).flatMap((scope) =>
    scope.activities.map((act) => ({
      ...act,
      scope_name: scope.scope_name,
    }))
  );
  const navigate = useNavigate();
  const { id } = useParams();
  const grouped = statusCategories.reduce((acc, status) => {
    acc[status.label] = flattenActivities.filter(
      (a) => (a.status || "").toLowerCase() === status.key
    );
    return acc;
  }, {});

  return (
    <Box
      sx={{
        mt: 3,
        display: "flex", // use flex instead of grid
        gap: 2,
        height: "60vh",
        overflowX: "auto", // horizontal scroll
        overflowY: "hidden",
        pb: 2,
        "&::-webkit-scrollbar": { height: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#ccc",
          borderRadius: 4,
        },
      }}
    >
      {statusCategories.map((status) => (
        <Box
          key={status.label}
          sx={{
            backgroundColor: "#F9FAFB",
            borderRadius: 2,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minWidth: 280, // ✅ fixed width for each Kanban column
            maxWidth: 280,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mb={2}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              pb: 1,
              flexShrink: 0,
              backgroundColor: "#F9FAFB",
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: status.color,
              }}
            />
            <Typography fontWeight={600}>{status.label}</Typography>
            <Typography sx={{ color: "#888", fontSize: 13, ml: "auto" }}>
              {grouped[status.label]?.length || 0}
            </Typography>
          </Box>

          {/* Cards Stack — scrollable */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: 3,
              },
            }}
          >
            {grouped[status.label]?.length ? (
              grouped[status.label].map((act) => {
                const effortDays =
                  act.start_date && act.end_date
                    ? moment(act.end_date).diff(
                        moment(act.start_date),
                        "days"
                      ) + 1
                    : null;
                return (
                <Card
                  key={act.id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                    flexShrink: 0,
                    height: 150,
                  }}
                  onClick={() =>
                    navigate(
                      `/project/details/${id}/schedule/details/${act.id}`
                    )
                  }
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" gap={2} alignItems="center" mb={1}>
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 35,
                          height: 35,
                          borderRadius: 1.5,
                          bgcolor: "#ececec",
                        }}
                        src="https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                      />
                      <Box>
                        <Typography fontWeight={600} fontSize={14}>
                          {act.milestone_name}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontSize={12}
                          color="textSecondary"
                        >
                          {act.scope_name}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        fontSize: 13,
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                        marginTop: "2vh",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ color: "#777", fontSize: 12 }}>
                          Effort
                        </span>
                        <span style={{ color: "#000", fontSize: 12 }}>
                          {effortDays != null ? `${effortDays} days` : "-"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ color: "#777", fontSize: 12 }}>
                          Duration
                        </span>
                        <span style={{ color: "#000", fontSize: 12 }}>
                          {act.start_date && act.end_date
                            ? `${moment(act.start_date).format(
                              "MMM D"
                            )} - ${moment(act.end_date).format("MMM D")}`
                            : "-"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "#777", fontSize: 12 }}>
                          Value
                        </span>
                        <span
                          style={{
                            color: "#000",
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {act.total_cost && <SmallDhiramIcon />}
                          {act.total_cost
                            ? `${formattedAmountWithoutAed(act.total_cost)}`
                            : "-"}
                        </span>
                      </div>
                    </Box>
                  </CardContent>
                </Card>
                );
              })
            ) : (
              <Typography
                sx={{
                  color: "#999",
                  fontSize: 13,
                  textAlign: "center",
                  mt: 2,
                }}
              >
                No activities
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ActivitiesKanbanView;
